import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  addMonths, brandDiscountFromRow, computeOverview, exclusivityConflicts, integrityFindings, lastDayOfMonth, overviewDealFromRow, overviewInvoiceFromRow, overviewPeriodFromRow,
  tiersFromJson, type ExclusivityConflict, type Overview, type OverviewFee,
} from "@talentkontor/domain";
import type { StaffWorkspace } from "../../../app/session";
import { api, emptyIfForbidden } from "../../../data";
import {
  fxConverter, useAgencyTerms, useBookings, useBrands, useContractStates, useDeals, useFxRates, useInvoices, useLineItems, useOrgSettings, usePeriods, useTalents,
  type AgencyTermsRow, type DealRow, type LineItemRow, type OrgSettingsRow, type PeriodRow,
} from "../../../data/queries";
import { monthLabel, monthName, today as todayIn } from "../../../lib/format";
import { useOrgConfig } from "../../../model/org-config";
import { viewInvoice, type InvoiceView, holidayRegionOf } from "../../../model/invoice-view";
import { t } from "../../../lib/sprache";
import { localDay } from "../vertraege/modell";

/** Daten und Rechnungen der Übersicht an einer Stelle (Chef-Ansicht und Manager-Ansicht). */

export type RangeKey = "6m" | "ytd" | "12m" | "prev" | "custom";

export function rangeOf(key: RangeKey, today: string, custom?: { from: string; to: string }): { from: string; to: string; label: string } {
  const cur = today.slice(0, 7);
  const year = today.slice(0, 4);
  const span = (from: string, to: string) =>
    from.slice(0, 4) === to.slice(0, 4) ? `${monthName(from)}–${monthName(to)} ${to.slice(0, 4)}` : `${monthLabel(from)} – ${monthLabel(to)}`;
  switch (key) {
    case "ytd": return { from: `${year}-01`, to: cur, label: span(`${year}-01`, cur) };
    case "12m": return { from: addMonths(cur, -11), to: cur, label: span(addMonths(cur, -11), cur) };
    case "prev": {
      const y = String(Number(year) - 1);
      return { from: `${y}-01`, to: `${y}-12`, label: t(`Januar–Dezember ${y}`, `January–December ${y}`) };
    }
    case "custom": {
      const from = custom?.from && custom.from <= (custom.to || cur) ? custom.from : addMonths(cur, -5);
      const to = custom?.to && custom.to >= from ? custom.to : cur;
      return { from, to, label: span(from, to) };
    }
    default: return { from: addMonths(cur, -5), to: cur, label: span(addMonths(cur, -5), cur) };
  }
}

export function feeOf(mode: string | null, bp: number | null, cents: number | null): OverviewFee {
  if (mode === "percent" && bp != null) return { mode: "percent", percent: bp / 100 };
  if (mode === "fixed" && cents != null) return { mode: "fixed", cents };
  return { mode: "none" };
}

export function useSetupStatus(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["org_setup_status", orgId],
    enabled,
    queryFn: () => api.read<{ complete?: boolean; missing?: { step: string; key: string }[] }>("org_setup_status", { p_org: orgId }),
  });
}

export function useDashboardData(ws: StaffWorkspace) {
  const orgId = ws.org.org_id;
  const invoices = useInvoices(orgId);
  // Provisionsprüfung ohne Monat (AD-218): provisionspflichtige Positionen der Creator-Rechnungen ohne Kooperationsmonat
  const noMonthIds = (invoices.data ?? []).filter((i) => i.kind === "talent_to_agency" && !i.period_id && i.deal_id && !["draft", "withdrawn", "discarded", "cancelled", "rejected"].includes(i.status))
    .map((i) => i.id).sort().slice(0, 300);
  const q = {
    settings: useOrgSettings(orgId), cfg: useOrgConfig(orgId), talents: useTalents(orgId), brands: useBrands(orgId), deals: useDeals(orgId),
    periods: usePeriods(orgId), extras: useLineItems(orgId), terms: useAgencyTerms(orgId), invoices,
    noMonthLines: useQuery({
      queryKey: ["invoice_lines", orgId, "ohne-monat", noMonthIds.join(",")], enabled: !!invoices.data,
      queryFn: () => noMonthIds.length
        ? api.select<{ invoice_id: string; net_cents: number; commission_free: boolean }>("invoice_lines", {
          columns: "invoice_id, net_cents, commission_free", filters: [["org_id", "eq", orgId], ["invoice_id", "in", noMonthIds]] })
        : Promise.resolve([] as { invoice_id: string; net_cents: number; commission_free: boolean }[]),
    }),
    bookings: useBookings(orgId), fx: useFxRates(), contracts: useContractStates(orgId),
    // Rahmenverträge und Sammelrechnungen (0047): Umsatzbeginn nach Vertrag und verbuchte Monate wie in den Kennzahlen
    koop: useQuery({
      queryKey: ["contract_deal_links", orgId, "uebersicht"],
      queryFn: async () => {
        const by = [["org_id", "eq", orgId]] as const;
        // Katalog der Plattformen und eigene Statusbezeichnungen der Agentur (Befund 15)
        // Nur fehlende Leserechte ergeben eine leere Liste; Lade- und Netzfehler zeigen „Erneut versuchen“ statt still falscher Kennzahlen (AD-016)
        const [links, periods, platforms, labels] = await Promise.all([
          emptyIfForbidden(api.select<{ deal_id: string; contract_id: string }>("contract_deal_links", { columns: "deal_id, contract_id", filters: by })),
          emptyIfForbidden(api.select<{ invoice_id: string; period_id: string }>("invoice_periods", { columns: "invoice_id, period_id", filters: by })),
          emptyIfForbidden(api.select<{ key: string; label_de: string; label_en: string | null }>("org_deal_platforms", { columns: "key, label_de, label_en", filters: by })),
          emptyIfForbidden(api.select<{ status: string; label_de: string | null; label_en: string | null }>("org_status_labels", { columns: "status, label_de, label_en", filters: by })),
        ]);
        return { links, periods, platforms, labels };
      },
    }),
  };
  const list = Object.values(q);
  const error = list.find((x) => x.error)?.error ?? null;
  const loading = list.some((x) => x.isPending);
  const today = todayIn(ws.org.timezone);
  const views = useMemo(() => {
    if (!q.cfg.data || !q.invoices.data || !q.periods.data) return [] as InvoiceView[];
    const periods = new Map(q.periods.data.map((p) => [p.id, p]));
    const v = { cfg: q.cfg.data, today, holidayRegion: holidayRegionOf(q.settings.data), periods };
    return q.invoices.data.map((r) => viewInvoice(r, v)).filter((x): x is InvoiceView => !!x);
  }, [q.cfg.data, q.invoices.data, q.periods.data, q.settings.data, today]);
  const retry = () => list.forEach((x) => x.error && void x.refetch());
  return { q, error, loading, today, views, retry };
}

type Q = ReturnType<typeof useDashboardData>["q"];

/** Spalten aus 0047/0060, die die gemeinsamen Zeilentypen nicht führen (select * liefert sie mit) */
type DealRowPlus = DealRow & { commission_tiers?: unknown; brand_discount_bp?: number | null; brand_discount_bearer?: string | null; invoice_route?: string | null; amounts_basis?: string | null;
  exclusive?: boolean; exclusive_until?: string | null; exclusive_scope?: string | null };
type PeriodRowPlus = PeriodRow & { outcome?: string | null; cancellation_fee_cents?: number | null };
type TermsRowPlus = AgencyTermsRow & { referral_mode?: string | null; referral_bp?: number | null; referral_cents?: number | null; referral_base?: string | null; referral_recipient?: string | null };

/** Kooperationen mit unterschriebenem Vertrag (Hauptbezug oder weitere Verknüpfung) für den Umsatzbeginn „Vertrag unterschrieben“. */
function signedDeals(q: Q): Set<string> {
  const contracts = q.contracts.data ?? [];
  const signedIds = new Set(contracts.filter((c) => c.status === "signed").map((c) => c.id));
  return new Set([...contracts.filter((c) => c.deal_id && c.status === "signed").map((c) => c.deal_id!),
    ...(q.koop.data?.links ?? []).filter((l) => signedIds.has(l.contract_id)).map((l) => l.deal_id)]);
}

/** Monate der Sammelrechnungen je Rechnung (invoice_periods, 0047) */
function linkedPeriods(q: Q): Map<string, string[]> {
  const linked = new Map<string, string[]>();
  for (const l of q.koop.data?.periods ?? []) linked.set(l.invoice_id, [...(linked.get(l.invoice_id) ?? []), l.period_id]);
  return linked;
}

export function useOverview(q: Q, args: { today: string; from: string; to: string; talentId: string; currency: string }): Overview | null {
  const settings = q.settings.data as OrgSettingsRow | null | undefined;
  return useMemo(() => {
    if (!settings || !q.deals.data || !q.periods.data || !q.extras.data || !q.invoices.data || !q.talents.data) return null;
    const terms = new Map((q.terms.data ?? []).map((t: AgencyTermsRow) => [t.deal_id, t as TermsRowPlus]));
    const brandRoute = new Map((q.brands.data ?? []).map((b) => [b.id, b.invoice_route ?? null]));
    // Umsatzbeginn nach Einstellung der Agentur (0047, 0060): Arbeitsstand oder unterschriebener Vertrag; sonst nur verbuchte Monate
    const signed = signedDeals(q);
    const linked = linkedPeriods(q);
    return computeOverview({
      today: args.today, from: args.from, to: args.to, talentId: args.talentId || null, currency: args.currency, orgCurrency: settings.currency,
      agencyCommissionPercent: (settings.default_commission_bp ?? 0) / 100,
      talentCommissionPercent: Object.fromEntries(q.talents.data.map((t) => [t.id, t.commission_bp == null ? null : t.commission_bp / 100])),
      agencyFee: feeOf(settings.agency_fee_mode, settings.agency_fee_bp, settings.agency_fee_cents),
      deals: q.deals.data.map((raw) => {
        const d = raw as DealRowPlus;
        const t = terms.get(d.id);
        return overviewDealFromRow(d, { fee: t ? feeOf(t.agency_fee_mode, t.agency_fee_bp, t.agency_fee_cents) : null, terms: t ?? null,
          brandRoute: brandRoute.get(d.brand_id) ?? null, orgRoute: settings.invoice_route, revenueStart: settings.revenue_start as string | null, signed: signed.has(d.id) });
      }),
      periods: q.periods.data.map((p) => overviewPeriodFromRow(p as PeriodRowPlus)),
      extras: q.extras.data.map((e) => ({ dealId: e.deal_id, periodId: e.period_id, amountCents: e.amount_cents, commissionable: e.commissionable,
        createdAt: (e as LineItemRow & { created_at?: string | null }).created_at ?? null })),
      // Eine Umwandlung für Übersicht, Kennzahlen und Stammdaten: mit Zahlungen (Teilzahlung) und Forderungsausfall
      invoices: q.invoices.data.map((i) => overviewInvoiceFromRow({ ...i, amount_org_cents: (i.amount_org_cents as number | null | undefined) ?? null,
        bad_debt_at: (i.bad_debt_at as string | null | undefined) ?? null }, linked.get(i.id))),
      bookings: (q.bookings.data ?? []).map((b) => ({ id: b.id, dealId: b.deal_id, periodId: b.period_id, talentId: b.talent_id, brandId: b.brand_id, kind: b.kind,
        bookedOn: b.booked_on, amountCents: b.amount_cents, currency: b.currency, agencyShareCents: b.agency_share_cents, status: b.status,
        fxRateToOrg: b.fx_rate_to_org ?? null, amountOrgCents: b.amount_org_cents ?? null })),
      convert: fxConverter(q.fx.data ?? []),
    });
  }, [settings, q.deals.data, q.periods.data, q.extras.data, q.invoices.data, q.talents.data, q.terms.data, q.bookings.data, q.fx.data, q.contracts.data, q.koop.data,
      args.today, args.from, args.to, args.talentId, args.currency]);
}

export function useIntegrity(q: Q, today: string, overview: Overview | null, talentId: string | null = null) {
  const settings = q.settings.data as OrgSettingsRow | null | undefined;
  return useMemo(() => {
    if (!settings || !q.deals.data || !q.periods.data || !q.invoices.data || !q.talents.data || !q.extras.data) return null;
    const byPeriod = new Map<string, number>();
    for (const o of overview?.occurrences ?? []) if (o.source === "deal") byPeriod.set(o.periodId, (byPeriod.get(o.periodId) ?? 0) + o.managementCents);
    const linked = linkedPeriods(q);
    const commissionable = new Map<string, number>();
    for (const l of q.noMonthLines.data ?? []) commissionable.set(l.invoice_id, (commissionable.get(l.invoice_id) ?? 0) + (l.commission_free ? 0 : Number(l.net_cents)));
    return integrityFindings({
      today,
      deals: q.deals.data.map((raw) => {
        const d = raw as DealRowPlus;
        return { id: d.id, talentId: d.talent_id, model: d.model, status: d.status, commissionPercent: d.commission_bp == null ? null : d.commission_bp / 100,
          commissionTiers: tiersFromJson(d.commission_tiers), brandDiscount: brandDiscountFromRow(d),
          // Nachlaufsatz je Abrechnungsmonat (0074) und Anteilsbeträge (0079), AD-218
          postTerm: d.post_term, postTermFrom: d.post_term_from ?? null, postTermUntil: d.post_term_until ?? null, amountsBasis: d.amounts_basis ?? null };
      }),
      // Stornierte Monate und Ausfallhonorare (Befund 1): gleiche Umwandlung wie für den Rechenkern
      periods: q.periods.data.map((raw) => {
        const p = raw as PeriodRowPlus;
        const o = overviewPeriodFromRow(p);
        return { id: p.id, dealId: p.deal_id, month: p.month, amountCents: p.amount_cents, invoiceRequestedAt: p.invoice_requested_at,
          commissionForecastCents: p.commission_forecast_cents, cancelled: !!o.cancelled, cancellationFee: !!o.cancellationFee,
          // Im bisherigen System abgerechnet: keine „Monate ohne Rechnung an die Marke“ (PRAXIS-R3-1)
          importedState: o.importedState ?? null };
      }),
      // Mit Kooperation: Positionen ohne Monat gehören zum ersten Monat (GELD-8)
      extras: q.extras.data.map((e) => ({ dealId: e.deal_id, periodId: e.period_id, amountCents: e.amount_cents, commissionable: e.commissionable })),
      // Sammelrechnungen rechnen jeden ihrer Monate ab (Befund 5)
      invoices: q.invoices.data.map((i) => ({ id: i.id, kind: i.kind, dealId: i.deal_id, periodId: i.period_id, talentId: i.talent_id, number: i.number,
        status: i.status, netCents: i.net_cents, createdAt: i.created_at, dueDate: i.effective_due_date, revenueMonth: i.revenue_month,
        rejectionMailSentAt: (i.rejection_mail_sent_at as string | null) ?? null, paidAt: i.paid_at, ...(linked.has(i.id) ? { periodIds: linked.get(i.id)! } : {}),
        // Dubletten und Provision ohne Monat (0161)
        creditFor: i.credit_for ?? null, commissionBpApplied: (i.commission_bp_applied as number | null | undefined) ?? null,
        servicePeriodStart: (i.service_period_start as string | null | undefined) ?? null,
        ...(commissionable.has(i.id) ? { commissionableNetCents: commissionable.get(i.id)! } : {}) })),
      agencyCommissionPercent: (settings.default_commission_bp ?? 0) / 100,
      talentCommissionPercent: Object.fromEntries(q.talents.data.map((t) => [t.id, t.commission_bp == null ? null : t.commission_bp / 100])),
      managementByPeriod: byPeriod,
      talentId,
      // Schwellen aus den Einstellungen der Agentur (sonst Produkt-Standard)
      deviationPercent: settings.budget_deviation_percent == null ? undefined : Number(settings.budget_deviation_percent),
      urgentDays: typeof settings.requested_urgent_days === "number" ? settings.requested_urgent_days : undefined,
      staleDraftDays: typeof settings.stale_draft_days === "number" ? settings.stale_draft_days : undefined,
      payoutAfterBrandPayment: settings.talent_payout_mode === "after_brand_payment",
      talentPaymentTermsDays: typeof settings.talent_payment_terms_days === "number" ? settings.talent_payment_terms_days : (settings.payment_terms_days ?? undefined),
      // Hinweis „abgerechnet ohne unterschriebenen Vertrag“, abschaltbar je Agentur (contract_warning = false)
      // Rahmenverträge hängen über contract_deal_links (0047) an mehreren Kooperationen; nur jüngere Abrechnungen (Befund 14)
      ...(settings.contract_warning === false ? {} : {
        contracts: contractsForIntegrity(q.contracts.data ?? [], q.koop.data?.links ?? [], (ts) => localDay(ts, settings.timezone) ?? ts.slice(0, 10)),
        contractsSinceMonth: addMonths(today.slice(0, 7), -(CONTRACT_HINT_MONTHS - 1)),
      }),
    });
  }, [settings, q.deals.data, q.periods.data, q.invoices.data, q.talents.data, q.extras.data, q.contracts.data, q.koop.data, q.noMonthLines.data, overview, today, talentId]);
}

/** Vertragshinweis nur für Abrechnungen der letzten sechs Leistungsmonate: ältere sind abgeschlossen und überfluten den Wächter (Befund 14) */
export const CONTRACT_HINT_MONTHS = 6;

/**
 * Exklusivitätskonflikte (Befund 9): laufende oder geplante Kooperationen eines Creators mit einer anderen Marke während einer
 * exklusiven Kooperation. Zeitraum aus den Monaten, sonst aus Beginn und Laufzeit.
 */
export function useExklusivitaet(q: Q, talentId: string | null = null): ExclusivityConflict[] {
  return useMemo(() => {
    if (!q.deals.data || !q.periods.data) return [];
    const brands = new Map((q.brands.data ?? []).map((b) => [b.id, b.name]));
    const list = exclusivityDeals(q.deals.data as DealRowPlus[], q.periods.data as PeriodRowPlus[]);
    return exclusivityConflicts(list, (id) => brands.get(id) ?? "").filter((c) => !talentId || c.talentId === talentId);
  }, [q.deals.data, q.periods.data, q.brands.data, talentId]);
}

/** Kooperationen mit Leistungszeitraum für die Exklusivitätsprüfung (ohne stornierte Monate). */
export function exclusivityDeals(deals: readonly DealRowPlus[], periods: readonly PeriodRowPlus[]) {
  const months = new Map<string, string[]>();
  for (const p of periods) if (p.status !== "cancelled") months.set(p.deal_id, [...(months.get(p.deal_id) ?? []), p.month.slice(0, 7)]);
  return deals.flatMap((d) => {
    const own = (months.get(d.id) ?? []).sort();
    const start = own[0] ?? d.start_month?.slice(0, 7);
    if (!start) return [];
    const end = own.at(-1) ?? addMonths(start, Math.max(1, d.months ?? 1) - 1);
    return [{ id: d.id, talentId: d.talent_id, brandId: d.brand_id, title: d.title, status: d.status, archived: !!d.archived_at, exclusive: !!d.exclusive,
      exclusiveUntil: d.exclusive_until ?? null, exclusiveScope: d.exclusive_scope ?? null, from: `${start}-01`, to: lastDayOfMonth(end) }];
  });
}

type IntegrityContract = { id: string; deal_id: string | null; status: string; ends_on?: string | null; auto_renew?: boolean | null;
  amends_id?: string | null; void_kind?: string | null; voided_at?: string | null; completed_at?: string | null };

/**
 * Verträge für den Wächter: Hauptbezug (contracts.deal_id) und jede weitere Verknüpfung (contract_deal_links, 0047). Ein
 * befristeter oder gekündigter Vertrag (Ende ohne automatische Verlängerung) deckt nur Leistungsmonate bis zu seinem Ende
 * (VERTRAEGE-R2-05). Ein Nachtrag endet spätestens mit seinem Ursprung (R3-02, wie app.contract_end_day). Ein einvernehmlich
 * aufgehobener Vertrag war bis zur Aufhebung wirksam und deckt die Monate bis zu ihrem Tag (R3-03); zurückgezogene und
 * irrtümlich erfasste decken nichts, ein ersetzter über die neue Fassung. `day` liefert den Tag eines Zeitpunkts in der Zone
 * der Agentur.
 */
export function contractsForIntegrity(contracts: readonly IntegrityContract[], links: readonly { deal_id: string; contract_id: string }[],
  day: (ts: string) => string = (ts) => ts.slice(0, 10)): { dealId: string | null; status: string; endsOn: string | null }[] {
  const all = new Map(contracts.map((c) => [c.id, c]));
  const min = (a: string | null, b: string | null) => (a && b ? (a < b ? a : b) : a ?? b);
  // wirksam und bis wann (null = ohne Ende); ein aufgehobener Ursprung beendet auch den Nachtrag
  const effect = (c: IntegrityContract, depth = 0): { covers: boolean; end: string | null } => {
    const fixed = c.ends_on && !c.auto_renew ? c.ends_on.slice(0, 10) : null;
    const covers = c.status === "signed" || (c.status === "void" && c.void_kind === "mutual" && !!c.completed_at);
    let end = c.status === "void" && c.voided_at ? min(fixed, day(c.voided_at)) : fixed;
    const origin = c.amends_id ? all.get(c.amends_id) : undefined;
    if (origin && depth < 5) end = min(end, effect(origin, depth + 1).end);
    return { covers, end };
  };
  const byId = new Map(contracts.map((c) => {
    const e = effect(c);
    return [c.id, { status: e.covers ? "signed" : c.status, endsOn: e.end }];
  }));
  return [
    ...contracts.map((c) => ({ dealId: c.deal_id, ...byId.get(c.id)! })),
    ...links.filter((l) => byId.has(l.contract_id)).map((l) => ({ dealId: l.deal_id, ...byId.get(l.contract_id)! })),
  ];
}
